import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import Logger from 'common/services/Logger';
import { useEffect, useState } from 'react';
import { Button, Container, Dropdown, Stack, Form, Card, Breadcrumb } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Reducers } from 'store/types';
import styles from './StoppingAreasScreen.module.scss';
import { StoppingAreaSearchCriteria } from 'api/stoppingAreas/models/StoppingAreaSearchCriteria';
import { Paged } from 'api/common/types/Page';
import { StoppingAreaDto } from 'api/stoppingAreas/models/StoppingAreaDto';
import StoppingAreasService from 'api/stoppingAreas/StoppingAreasService';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import Loading from 'common/services/Loading';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';

type Props = {
};

const StoppingAreasScreen: React.FC<Props> = () => {
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const [criteria, setCriteria] = useState<StoppingAreaSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderBy: 'asc',
        orderColumn: 'name'
    });
    const [stoppingAreasPage, setStoppingAreasPage] = useState<Paged<StoppingAreaDto>>();
    const [filters, setFilters] = useState({
        allIn: ''
    });
    const hasStoppingAreasWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['STOPPING_AREAS_WRITE']);
    const { t } = useTranslation();
    const navigate = useNavigate();

    const getData = async () => {
        try {
            Loading.show();
            const page = await StoppingAreasService.getList(criteria);
            setStoppingAreasPage(page);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get stopping areas', error);
            toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    }

    const renderTableActionCell = (row: StoppingAreaDto) => {
        return <Dropdown>
            <Dropdown.Toggle id="dropdown-basic-button" size="sm" variant="outline-secondary">...</Dropdown.Toggle>
            <Dropdown.Menu popperConfig={{ strategy: 'fixed' }} renderOnMount>
                <Dropdown.Item onClick={() => navigate(`/stoppingarea/details/${row.id}`)}>{t('common.details')}</Dropdown.Item>
                { hasStoppingAreasWritePolicy && <>
                <Dropdown.Item onClick={() => navigate(`/stoppingarea/edit/${row.id}`)}>{t('common.edit')}</Dropdown.Item>
                <Dropdown.Item onClick={() => showRemoveItemDialog(row)}>{t('common.delete')}</Dropdown.Item>
                </>
                }
            </Dropdown.Menu>
        </Dropdown>
    };

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field });
    }

    const tableColumns: ListingTableColumn<StoppingAreaDto>[] = [
        {
            field: 'name',
            name: t('stopping_areas.list.name') ?? '',
            onSearch: onTableFilter,
            searchField: 'a.name'
        },
        {
            name: '',
            width: '60px',
            cellAlignment: 'right',
            preventClick: true,
            renderCell: renderTableActionCell,
            cellStyle: { overflow: 'unset' }
        },
    ];

    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<StoppingAreaDto | null>(null);

    const showRemoveItemDialog = async (item: StoppingAreaDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onRowClick = (event: any, id?: string) => {
        if (id) {
            if (event.ctrlKey) {
                window.open(location.pathname + '/details/' + id, '_blank');
                return;
            }
            navigate(`/stoppingarea/details/${id}`);
        }
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            toast.error(t('messages.record_delete_error'));
            return;
        }
        try {
            await StoppingAreasService.remove(itemToRemove);
            onCancelRemove();
            toast.success(t('messages.record_delete_success'));
            setCriteria({ ...criteria, page: 1 })
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete api_tokens', error);
            toast.error(t('messages.record_delete_error'));
        }
    };

    useEffect(() => {
        getData();
    }, [criteria]);

    function handleChange(e: any) {
        const key = e.target.name;
        const value = e.target.value;
        setFilters({ ...filters, [key]: value });
    }
    function clearFilters() {
        setFilters({ allIn: '' });
        setCriteria({ ...criteria, page: 1, allIn: '' });
    }
    const onSearch = () => {
        setCriteria({ ...criteria, allIn: filters.allIn });
    }

    return (
        <ScreenTitle title={t('stopping_areas.title')}>
                <Breadcrumb>
                    <Breadcrumb.Item active>{t('stopping_areas.title')}</Breadcrumb.Item>
                </Breadcrumb>
                <Container fluid>
                <Card className={styles.filters}>
                    <Card.Body>
                        <Stack direction="horizontal" gap={3} >
                            <Form.Control type="text" value={filters.allIn} className="me-auto" name="allIn" onChange={handleChange} onKeyDown={(e: any) => { if (e.key === 'Enter') onSearch(); }} placeholder={t('common.write_here')} />
                            <Button variant="secondary" onClick={onSearch}>{t('common.search')}</Button>
                            <div className="vr" />
                            <Button variant="outline-danger" onClick={clearFilters}>{t('common.clear')}</Button>
                            <Button variant="primary" onClick={() => navigate('/stoppingarea/new/')}>{t('common.new')}</Button>
                        </Stack>
                    </Card.Body>
                </Card>
            </Container>
            <Container fluid>
                <ListingTable
                    columns={tableColumns}
                    rows={stoppingAreasPage?.items || []}
                    onRowClick={(row, _, event) => onRowClick(event, row.id)}
                    allowHover={true}
                    initialSearch={{ colField: 'name', isOrderAsc: true }}
                />
                <PaginationWithInfo
                    itemName={t('stopping_areas.title')}
                    currentPage={stoppingAreasPage?.currentPage ?? 1}
                    pageItems={stoppingAreasPage?.items.length || 0}
                    totalItems={stoppingAreasPage?.totalItems || 0}
                    onChange={page => setCriteria({ ...criteria, page: page })}
                />
            </Container>
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('messages.remove_record_with_ident', { name: itemToRemove?.name ?? '' })} />
        </ScreenTitle>
    );
};

export default StoppingAreasScreen;
